import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../core/Auth';
import { expiredPassword, recoverPassword } from '../core/_requests';
import { toastHelper } from '../../global/toastrHelper';
import { toAbsoluteUrl } from '../../../../_metronic/helpers/AssetHelpers';

const RecoverPasswordForm = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Try to get from sessionStorage first (user with expired password), then from localStorage
    const tempUser = sessionStorage.getItem('tempUser');
    const storedUser = localStorage.getItem('user');
    const currentUser: any = tempUser ? JSON.parse(tempUser) : (storedUser ? JSON.parse(storedUser) : null);

    // If there's no user, redirect to login page
    React.useEffect(() => {
        if (!currentUser) {
            console.log("🚀 ~ No user found, redirecting to /auth")
            navigate('/auth');
        }
    }, [currentUser, navigate]);

    const [password, setPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const passwordCriteria = [
        { label: 'Mínimo de 8 caracteres', check: (pwd: string) => pwd.length >= 8 },
        { label: 'Pelo menos uma letra maiúscula', check: (pwd: string) => /[A-Z]/.test(pwd) },
        { label: 'Pelo menos uma letra minúscula', check: (pwd: string) => /[a-z]/.test(pwd) },
        { label: 'Pelo menos um número', check: (pwd: string) => /\d/.test(pwd) },
        { label: 'Pelo menos um caractere especial', check: (pwd: string) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd) },
    ];


    const getInitialValues = (user) => {
        console.log("🚀 ~ RecoverPasswordForm ~ currentUser:", currentUser)
        console.log("🚀 ~ getInitialValues ~ !user.trocarSenhaTemporaria:", user ? !user.trocarSenhaTemporaria : 'user is null')
        
        // If there's no user, redirect to login page
        if (!user) {
            console.log("🚀 ~ No user found, redirecting to /auth")
            navigate('/auth');
            return {
                oldPassword: '',
                newPassword: '',
                confirmPassword: '',
            };
        }
        
        if (user.trocarSenhaTemporaria) {
            return {
                newPassword: '',
                confirmPassword: '',
            };
        } else {
            return {
                oldPassword: '',
                newPassword: '',
                confirmPassword: '',
            };
        }
    };

    const getValidationSchema = (user) => {
        // If there's no user, return basic schema
        if (!user) {
            return Yup.object({
                newPassword: Yup.string().required('Nova senha é obrigatória'),
                confirmPassword: Yup.string()
                    .oneOf([Yup.ref('newPassword')], 'As senhas devem coincidir')
                    .required('Confirmação da nova senha é obrigatória'),
            });
        }

        const schema: any = {
            // newPassword: Yup.string()
            //     .min(6, 'A nova senha deve ter no mínimo 8 caracteres')
            //     .required('Nova senha é obrigatória'),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref('newPassword')], 'As senhas devem coincidir')
                .required('Confirmação da nova senha é obrigatória'),
        };

        if (!user.trocarSenhaTemporaria) {
            schema.oldPassword = Yup.string().required('Senha antiga é obrigatória');
        }

        return Yup.object(schema);
    };

    const formik = useFormik({
        initialValues: getInitialValues(currentUser),
        validationSchema: getValidationSchema(currentUser),
        onSubmit: async (values, { setErrors, setSubmitting }) => {
            try {
                console.log("🚀 ~ Enviando requisição de troca de senha...", values);

                // If there's no user, redirect to login
                if (!currentUser) {
                    console.log("🚀 ~ No user found during submit, redirecting to /auth")
                    navigate('/auth');
                    return;
                }

                let resp;
                if (currentUser.trocarSenhaExpirada || currentUser.trocarSenhaTemporaria) {
                    resp = await expiredPassword(currentUser, values);
                } else {
                    resp = await recoverPassword(currentUser, values);
                }

                console.log("🚀 ~ Resposta da API:", resp);
                // Clear both localStorage and sessionStorage
                localStorage.removeItem('user');
                sessionStorage.removeItem('tempUser');
                toastHelper.success('Senha atualizada com sucesso');
                navigate('/cursos');
            } catch (error: any) {
                console.error("🚀 ~ Erro capturado da API:", error.response);

                if (error.response && error.response.status === 400) {
                    setErrors({ newPassword: error.response.data.message });
                } else {
                    toastHelper.error("Erro inesperado. Tente novamente.");
                }

                setSubmitting(false);
            }
        },

    });
    const handleSubmitWithParams = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        formik.handleSubmit();
    };
    const isPasswordValid = passwordCriteria.every((criterion) => criterion.check(password));
    const isFormValid = isPasswordValid && formik.values.newPassword === formik.values.confirmPassword;


    return (
        <div>
            <form
                className='form w-100'
                onSubmit={formik.handleSubmit}
                noValidate
                id='kt_password_recovery_form'
            >
                <div className='text-center'>
                    <img src={toAbsoluteUrl('/media/assemp/assemp-logo-2.svg')} style={{ mixBlendMode: 'multiply' }} className='logo' />
                </div>
                <div className='text-center headline'>
                    <h1 className='mb-0'>
                        Redefina sua senha
                    </h1>
                    <p>
                        Insira sua nova senha para concluir a recuperação de senha
                    </p>
                </div>


                {/* begin::Form group */}
                {/* Conditionally render the old password field */}
                {currentUser && !currentUser.trocarSenhaTemporaria && (
                    <div className='fv-row mb-8'>
                        <label className='form-label fs-6 fw-bolder'>Senha Antiga</label>
                        <input
                            placeholder='Senha antiga'
                            {...formik.getFieldProps('oldPassword')}
                            className={clsx(
                                'form-control bg-transparent',
                                { 'is-invalid': formik.touched.oldPassword && formik.errors.oldPassword },
                                { 'is-valid': formik.touched.oldPassword && !formik.errors.oldPassword }
                            )}
                            type='password'
                            name='oldPassword'
                            autoComplete='off'
                        />
                        {formik.touched.oldPassword && formik.errors.oldPassword && (
                            <div className='fv-plugins-message-container'>
                                <span role='alert' style={{ color: 'red' }}>{formik.errors.oldPassword}</span>
                            </div>
                        )}
                    </div>
                )}
                {/* end::Form group */}

                <hr className='my-8'/>

                {/* begin::Form group */}
                <div className='fv-row mb-8'>
                    <label className='form-label fs-6 fw-bolder'>Nova Senha</label>
                    <div className="input-group">
                        <input
                            type={showNewPassword ? 'text' : 'password'}
                            className="form-control custom-input"
                            placeholder="Nova senha"
                            {...formik.getFieldProps('newPassword')}
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                formik.handleChange(e);
                            }}
                            style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: '0' }}
                            autoComplete="off"
                        />
                        <span
                            className="input-group-text"
                            style={{ cursor: 'pointer', background: '#fff', borderLeft: '0' }}
                            onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                            <img
                                src={showNewPassword ? "/media/icons/show.svg" : "/media/icons/hide.svg"}
                                alt={showNewPassword ? "Esconder senha" : "Mostrar senha"}
                                width="20"
                                height="20"
                            />
                        </span>
                    </div>

                    {formik.touched.newPassword && formik.errors.newPassword && (
                        <div className='fv-plugins-message-container'>
                            <span role='alert' style={{ color: 'red' }}>{formik.errors.newPassword}</span>
                        </div>
                    )}
                </div>
                {/* end::Form group */}

                {/* begin::Form group */}
                <div className='fv-row mb-8'>
                    <label className='form-label fs-6 fw-bolder'>Confirmação de Nova Senha</label>
                    <div className="input-group">
                        <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            className="form-control custom-input"
                            placeholder="Confirmação de nova senha"
                            {...formik.getFieldProps('confirmPassword')}
                            style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: '0' }}
                            autoComplete="off"
                        />
                        <span
                            className="input-group-text"
                            style={{ cursor: 'pointer', background: '#fff', borderLeft: '0' }}
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                            <img
                                src={showConfirmPassword ? "/media/icons/show.svg" : "/media/icons/hide.svg"}
                                alt={showConfirmPassword ? "Esconder senha" : "Mostrar senha"}
                                width="20"
                                height="20"
                            />
                        </span>

                    </div>
                    <ul className="password-requirements list-inline">
                        {passwordCriteria.map((criterion, index) => (
                            <li key={index}>
                                <span style={{ backgroundColor: criterion.check(password) ? '#009FE3' : '#9B9EA6' }}>
                                    {criterion.check(password) ? <i className='stepper-check fas fa-check'></i> : <i className='stepper-check fas fa-times'></i>} 
                                </span>
                                {criterion.label}
                            </li>
                        ))}
                        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                            <li>
                                <span style={{ backgroundColor: '#F56565' }}>
                                    <i className='stepper-check fas fa-times'></i>
                                </span>
                                <div style={{ color: '#F56565' }}>
                                    {formik.errors.confirmPassword}
                                </div>
                            </li>
                        )}
                    </ul>
                </div>

                {/* end::Form group */}

                {/* begin::Action */}
                <div className='d-grid mb-10'>
                    <button
                        type='submit'
                        id='kt_password_recovery_submit'
                        className='btn btn-primary'
                        style={{ backgroundColor: '#009FE3' }}
                        disabled={formik.isSubmitting || !isFormValid}
                    >
                        {!formik.isSubmitting && <span className='indicator-label'>Atualizar Senha</span>}
                        {formik.isSubmitting && (
                            <span className='indicator-progress' style={{ display: 'block' }}>
                                Carregando...
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                            </span>
                        )}
                    </button>

                    {/* <button onClick={() => navigate('/cursos')}>GOTO HOME</button> */}
                </div>
                {/* end::Action */}
            </form>
        </div>
    );
};

export default RecoverPasswordForm;